<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { resolveScan, useScanner, type ScanTarget } from '@/lib/scan';

/**
 * Scan anything, land on the screen that is about it.  design.md §11.6
 *
 * Mounted once in App.vue, so it works everywhere: somebody holding a scanner
 * does not want to navigate to the right page before scanning — finding the
 * right page is what they are scanning for.
 *
 * ── A SCAN IS NEVER SILENT ─────────────────────────────────────────────────
 *
 * The page changing under somebody is startling if nothing says why, and a scan
 * that matched nothing is indistinguishable from a scanner that is switched off.
 * So every scan says what it read and what it did, including the ones that did
 * nothing — the banner is the whole difference between a feature and a haunting.
 *
 * ── AND IT NEVER GUESSES ───────────────────────────────────────────────────
 *
 * Two locations with the same address, or two products matching one string, is
 * a question rather than a coin toss. Dropping a picker on an arbitrary one of
 * two candidates is worse than telling them there were two, because they will
 * believe it.
 */

const router = useRouter();

interface Notice {
  code: string;
  said: string;
  /** A refusal stays until it is dismissed; an answer fades. */
  stuck: boolean;
  choices?: { label: string; to: string }[];
}

const notice = ref<Notice | null>(null);
let fade: ReturnType<typeof setTimeout> | undefined;

function say(next: Notice): void {
  clearTimeout(fade);
  notice.value = next;
  // Four seconds: long enough to read seven words while walking, short enough
  // that it is gone before the next scan.
  if (!next.stuck) fade = setTimeout(() => (notice.value = null), 4000);
}

function describe(target: ScanTarget): Notice {
  switch (target.kind) {
    case 'location':
      return {
        code: target.code,
        said: `${target.location.path} · ${target.location.siteCode}`,
        stuck: false,
      };
    case 'locations':
      // The same address in two buildings. Ask.
      return {
        code: target.code,
        said: `${target.locations.length} places are called that`,
        stuck: true,
        choices: target.locations.map((l) => ({
          label: `${l.path} · ${l.siteCode}`,
          to: `/warehouse?at=${encodeURIComponent(l.id)}`,
        })),
      };
    case 'product':
      return {
        code: target.code,
        said: target.orderNo ? `${target.sku} · order ${target.orderNo}` : target.sku,
        stuck: false,
        // A part label knows which order it went out on, and "which order was
        // this in" is the question somebody holding a returned bag is asking.
        choices: target.orderNo
          ? [
              { label: `Product ${target.sku}`, to: `/catalog/${encodeURIComponent(target.sku)}` },
              {
                label: `Order ${target.orderNo}`,
                to: `/orders/${encodeURIComponent(target.orderNo)}`,
              },
            ]
          : undefined,
      };
    case 'unknown':
      return { code: target.code, said: 'nothing matches that', stuck: true };
  }
}

async function handle(code: string): Promise<void> {
  say({ code, said: 'looking…', stuck: true });
  const target = await resolveScan(code);
  const next = describe(target);
  say(next);

  // Navigate only when there is one answer. `locations` is always a question;
  // `product` with an order offers both and goes to the product, which is the
  // one that is true of the label whatever it was scanned for.
  if (target.kind === 'location') {
    await router.push(`/warehouse?at=${encodeURIComponent(target.location.id)}`);
  } else if (target.kind === 'product') {
    await router.push(`/catalog/${encodeURIComponent(target.sku)}`);
  }
}

useScanner((code) => void handle(code));

function dismiss(): void {
  clearTimeout(fade);
  notice.value = null;
}

function go(to: string): void {
  dismiss();
  void router.push(to);
}
</script>

<template>
  <!-- NO <Transition>. It was a 120 ms fade and it cost the one property this
       banner has to have: that it goes away. Vue's Transition advances its
       classes on requestAnimationFrame, so in a tab that is not rendering —
       backgrounded, or a second monitor somebody is not looking at — the
       element sticks with `enter-from` and `leave-active` at once and the
       dismiss button does nothing. Found by pressing it.

       A status message that reports a scan is exactly the wrong place to trade
       reliability for a fade. It appears and it goes; the animation below is a
       CSS keyframe on the element itself, which owes nothing to a framework. -->
  <Teleport to="body">
    <div v-if="notice" class="scan" role="status" aria-live="polite">
      <span class="scan__icon" aria-hidden="true">⌖</span>
      <span class="scan__code code">{{ notice.code }}</span>
      <span class="scan__said">{{ notice.said }}</span>

      <span v-if="notice.choices" class="scan__choices">
        <button v-for="c in notice.choices" :key="c.to" class="scan__go" @click="go(c.to)">
          {{ c.label }}
        </button>
      </span>

      <button v-if="notice.stuck" class="scan__close" aria-label="Dismiss" @click="dismiss">
        ×
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
/* Teleported and fixed for the same reason every other overlay here is: a
   transformed ancestor would trap it. Top-centre because it is read while
   looking at the thing that was just scanned, not at the corner of a screen. */
.scan {
  position: fixed;
  top: 12px;
  left: 50%;
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(720px, calc(100vw - 24px));
  padding: 8px 12px;
  transform: translateX(-50%);
  border: 1px solid var(--color-line-strong);
  border-radius: 6px;
  background: var(--color-paper);
  box-shadow: 0 6px 20px rgb(0 0 0 / 14%);
  font-size: 13px;
}
.scan__icon {
  color: var(--color-amber-ink);
}
.scan__code {
  font-size: 12px;
  color: var(--color-ink-muted);
  max-width: 24ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.scan__said {
  font-weight: 700;
}
.scan__choices {
  display: flex;
  gap: 6px;
}
.scan__go {
  padding: 2px 8px;
  border: 1px solid var(--color-line-strong);
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}
.scan__go:hover {
  border-color: var(--color-ink);
}
.scan__close {
  margin-left: 2px;
  padding: 0 4px;
  color: var(--color-ink-muted);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}

/* A keyframe on the element, not a Vue transition — and one that NEVER TOUCHES
   OPACITY. Both matter, and both were learned the same way.

   The Vue transition stuck: it advances its classes on requestAnimationFrame,
   so in a tab that is not rendering the element kept `enter-from` and
   `leave-active` at once and the dismiss button did nothing. Replacing it with
   a keyframe that faded in from `opacity: 0` fixed the dismiss and left the
   banner invisible, stuck on its own first frame, for exactly the same reason.

   So it slides and nothing else. Frozen at frame zero it is a readable banner
   six pixels too high, which is the only acceptable way for a status message
   to fail. */
@keyframes scan-in {
  from {
    transform: translate(-50%, -6px);
  }
}
.scan {
  animation: scan-in 0.12s ease-out;
}
@media (prefers-reduced-motion: reduce) {
  .scan {
    animation: none;
  }
}
</style>
